import { rem } from '@mantine/core';
import { noteNeedsViewer, noteSide } from '@reditelji/shared';
import { useState } from 'react';
import { useSession } from '../api/session';
import type { TagRow } from '../api/tags';
import type { VolunteerRow } from '../api/volunteers';
import { ini } from '../lib/ui';
import type { VolunteerRollup } from './derive';
import { recordedOn } from './cancelCopy';
import { hoursLabel, plIzmen } from './derive';
import { RowTagChips, TagFilterChips, TagPicker, useApplyVolunteerTags } from './TagBits';
import { INPUT, MONO, seg } from './ui';
import { ViewSwitch, type RosterView } from './ViewSwitch';

export type QuickFilter = 'free' | 'clash' | 'stale';

export interface SidebarProps {
  rows: { v: VolunteerRow; r: VolunteerRollup }[];
  shown: { v: VolunteerRow; r: VolunteerRollup }[];
  /** People who are not coming (SPEC §1.1K): apart, at the foot, collapsed. */
  out: { v: VolunteerRow; r: VolunteerRollup }[];
  /** Of those, the ones the search finds. */
  outShown: { v: VolunteerRow; r: VolunteerRollup }[];
  tags: TagRow[];
  tagUsage: Map<string, number>;
  search: string;
  setSearch: (q: string) => void;
  active: Set<QuickFilter>;
  toggleFilter: (f: QuickFilter) => void;
  tagFilter: string[];
  toggleTagFilter: (id: string) => void;
  clearTagFilter: () => void;
  personId: string | null;
  pick: (id: string) => void;
  selectMode: boolean;
  toggleSelectMode: () => void;
  selected: Set<string>;
  setSelected: (s: Set<string>) => void;
  openTagManager: () => void;
  newPerson: () => void;
  openImport: () => void;
  openTriage: () => void;
  triageCount: number;
  /** The screen's second list — external teams (SPEC §1.1J). */
  setView: (v: RosterView) => void;
  teamCount: number;
}

const FILTERS: { key: QuickFilter; label: string }[] = [
  { key: 'free', label: 'brez izmen' },
  { key: 'clash', label: 'prekrivanja' },
  { key: 'stale', label: 'brez odgovora' },
];

export function Sidebar(p: SidebarProps) {
  const [bulkOpen, setBulkOpen] = useState(false);
  const applyTags = useApplyVolunteerTags();
  // Collapsed unless asked: opened by hand, by a search that finds somebody in
  // it, or because the open card is one of theirs. `null` means nobody asked.
  const [outOpen, setOutOpen] = useState<boolean | null>(null);
  const searching = p.search.trim() !== '';
  const outExpanded = searching
    ? p.outShown.length > 0
    : (outOpen ?? p.out.some((x) => x.v.id === p.personId));
  const outList = searching ? p.outShown : p.out;
  const counts: Record<QuickFilter, number> = {
    free: p.rows.filter((x) => x.r.shifts.length === 0).length,
    clash: p.rows.filter((x) => x.r.clash).length,
    stale: p.rows.filter((x) => x.r.noReply).length,
  };
  // The organisation searches and reads the roster; editing it, its tags and
  // its mailbox are the marshal team's, and "brez odgovora" is send status (SPEC §1.1L).
  const { me, can } = useSession();
  const mySide = me ? noteSide(me.role) : 'redarji';
  const canEdit = can('people:write');
  const filters = FILTERS.filter((f) => f.key !== 'stale' || can('comms:read'));
  const selCount = p.selected.size;
  const bulkTargets = p.rows.filter((x) => p.selected.has(x.v.id)).map((x) => x.v);
  const selLabel =
    `${selCount} ` +
    (selCount === 1 ? 'izbran' : selCount === 2 ? 'izbrana' : selCount === 3 || selCount === 4 ? 'izbrani' : 'izbranih');

  const smallBtn: React.CSSProperties = {
    cursor: 'pointer',
    padding: '5px 8px',
    borderRadius: 6,
    fontSize: rem(12),
    fontFamily: 'inherit',
    background: 'oklch(1 0 0)',
    color: 'oklch(0.45 0.012 90)',
    border: '1px solid oklch(0.91 0.006 95)',
    whiteSpace: 'nowrap',
  };

  return (
    <div
      style={{
        width: 306,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'oklch(1 0 0)',
        borderRight: '1px solid oklch(0.9 0.006 95)',
        minHeight: 0,
      }}
    >
      <ViewSwitch
        view="ljudje"
        setView={p.setView}
        people={p.rows.length}
        teams={p.teamCount}
      />

      <div
        style={{
          padding: '12px 12px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          borderBottom: '1px solid oklch(0.94 0.005 95)',
        }}
      >
        <input
          value={p.search}
          onChange={(e) => p.setSearch(e.currentTarget.value)}
          placeholder="Išči ime, telefon, kraj, oznako…"
          style={{ ...INPUT, width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: 'oklch(0.985 0.004 95)' }}
        />
        <div style={{ display: 'flex', gap: 5 }}>
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => p.toggleFilter(f.key)}
              style={{
                flex: 1,
                cursor: 'pointer',
                padding: '5px 8px',
                borderRadius: 6,
                fontSize: rem(12.5),
                fontFamily: 'inherit',
                ...seg(p.active.has(f.key)),
              }}
            >
              {f.label} {counts[f.key]}
            </button>
          ))}
        </div>
        <TagFilterChips
          tags={p.tags}
          usage={p.tagUsage}
          filter={p.tagFilter}
          onToggle={p.toggleTagFilter}
          onClear={p.clearTagFilter}
        />
        {canEdit ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={p.toggleSelectMode}
                style={{ ...smallBtn, flex: 1, ...(p.selectMode ? seg(true) : {}) }}
              >
                {p.selectMode ? 'Končaj izbiranje' : 'Označi več'}
              </button>
              <button onClick={p.openTagManager} style={{ ...smallBtn, flex: 1 }}>
                Uredi oznake
              </button>
            </div>
            <button
              onClick={p.newPerson}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                cursor: 'pointer',
                padding: 8,
                borderRadius: 7,
                border: '1px solid oklch(0.85 0.05 245)',
                background: 'oklch(0.965 0.025 245)',
                color: 'oklch(0.4 0.08 245)',
                fontSize: rem(13.5),
                fontWeight: 600,
                fontFamily: 'inherit',
              }}
            >
              + Nov prostovoljec
            </button>
          </>
        ) : null}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
        {p.shown.map(({ v, r }) => {
          const on = p.personId === v.id && !p.selectMode;
          const checked = p.selected.has(v.id);
          const visTags = p.tags.filter((t) => v.tagIds.includes(t.id));
          const meta = [
            v.residence || null,
            r.shifts.length ? `${plIzmen(r.shifts.length)} · ${hoursLabel(r.minutes)}` : 'brez izmen',
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div
              key={v.id}
              onClick={() => {
                if (p.selectMode) {
                  const next = new Set(p.selected);
                  if (checked) next.delete(v.id);
                  else next.add(v.id);
                  p.setSelected(next);
                } else p.pick(v.id);
              }}
              style={{
                display: 'flex',
                gap: 9,
                alignItems: 'center',
                padding: '8px 10px',
                borderRadius: 8,
                cursor: 'pointer',
                background: checked ? 'oklch(0.968 0.022 245)' : on ? 'oklch(0.965 0.02 245)' : 'oklch(1 0 0)',
                border: `1px solid ${checked || on ? 'oklch(0.86 0.04 245)' : 'oklch(0.96 0.005 95)'}`,
                marginBottom: 4,
              }}
            >
              {p.selectMode ? (
                <span
                  style={{
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                    borderRadius: 4,
                    background: checked ? 'oklch(0.5 0.09 245)' : 'oklch(1 0 0)',
                    border: `1px solid ${checked ? 'oklch(0.5 0.09 245)' : 'oklch(0.85 0.008 95)'}`,
                    color: 'oklch(1 0 0)',
                    fontSize: rem(11),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {checked ? '✓' : ''}
                </span>
              ) : null}
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: r.clash ? 'oklch(0.94 0.04 25)' : 'oklch(0.94 0.02 245)',
                  color: r.clash ? 'oklch(0.45 0.11 25)' : 'oklch(0.42 0.06 245)',
                  fontSize: rem(11),
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {ini(v.name)}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: rem(13.5), fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {v.name}
                </div>
                <div style={{ fontSize: rem(11.5), color: 'oklch(0.6 0.012 90)' }}>{meta}</div>
                <RowTagChips tags={visTags} />
              </div>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {r.clash ? (
                  <span
                    title="prekrivanje izmen"
                    style={{
                      fontFamily: MONO,
                      fontSize: rem(11),
                      fontWeight: 700,
                      color: 'oklch(1 0 0)',
                      background: 'oklch(0.55 0.14 25)',
                      borderRadius: 3,
                      padding: '1px 3px',
                    }}
                  >
                    2×
                  </span>
                ) : null}
                {me && noteNeedsViewer(me.role, v) ? (
                  <span
                    title={
                      mySide === 'redarji'
                        ? 'organizacija čaka odgovor v zavihku Organizacija'
                        : 'ekipa redarjev je odgovorila v zavihku Organizacija'
                    }
                    style={{
                      fontFamily: MONO,
                      fontSize: rem(10.5),
                      fontWeight: 700,
                      color: 'oklch(1 0 0)',
                      background: 'oklch(0.58 0.13 25)',
                      borderRadius: 10,
                      padding: '1px 5px',
                    }}
                  >
                    opomba
                  </span>
                ) : null}
                {r.briefStale ? (
                  <span
                    title={
                      r.publication === 'brez objave'
                        ? 'razpored še ni objavljen — oseba ne vidi svojih izmen'
                        : 'oseba vidi starejši razpored od trenutnega'
                    }
                    style={{
                      fontSize: rem(12),
                      color:
                        r.publication === 'brez objave'
                          ? 'var(--rr-warn-strong)'
                          : 'oklch(0.6 0.1 75)',
                    }}
                  >
                    ✉
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
        {p.out.length > 0 && !p.selectMode ? (
          <div style={{ marginTop: 8, paddingTop: 4, borderTop: '1px dashed oklch(0.88 0.05 25)' }}>
            <button
              type="button"
              aria-expanded={outExpanded}
              onClick={() => setOutOpen(!outExpanded)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                margin: '4px 0 6px',
                padding: '5px 6px',
                border: 0,
                background: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: rem(11),
                fontWeight: 800,
                letterSpacing: '0.06em',
                color: 'oklch(0.5 0.13 25)',
              }}
            >
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  transition: 'transform 150ms ease-out',
                  transform: outExpanded ? 'rotate(90deg)' : 'none',
                }}
              >
                ▸
              </span>
              NE PRIDEJO · {p.out.length}
            </button>
            {outExpanded
              ? outList.map(({ v, r }) => {
                  const on = p.personId === v.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => p.pick(v.id)}
                      style={{
                        display: 'flex',
                        gap: 9,
                        alignItems: 'center',
                        padding: '8px 10px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        background: on ? 'oklch(0.975 0.025 25)' : 'oklch(0.99 0.012 25)',
                        border: `1px solid ${on ? 'oklch(0.82 0.08 25)' : 'oklch(0.92 0.035 25)'}`,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: '50%',
                          background: 'oklch(0.94 0.045 25)',
                          color: 'oklch(0.45 0.12 25)',
                          fontSize: rem(11),
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {ini(v.name)}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: rem(13.5),
                            fontWeight: 600,
                            color: 'oklch(0.42 0.12 25)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {v.name}
                        </div>
                        <div style={{ fontSize: rem(11.5), color: 'oklch(0.55 0.08 25)' }}>
                          zabeleženo {recordedOn(v.cancelledAt)}
                          {r.shifts.length ? ` · ${plIzmen(r.shifts.length)} · ${hoursLabel(r.minutes)}` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })
              : null}
          </div>
        ) : null}
        {p.shown.length === 0 && !(searching && p.outShown.length > 0) ? (
          <div style={{ padding: '18px 10px', fontSize: rem(13), color: 'oklch(0.6 0.012 90)' }}>
            Ni zadetkov za izbrane filtre.
          </div>
        ) : null}
      </div>

      {p.selectMode ? (
        <div
          style={{
            flexShrink: 0,
            borderTop: '1px solid oklch(0.92 0.006 95)',
            background: 'oklch(0.988 0.003 95)',
            padding: '9px 10px',
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: rem(12.5), fontWeight: 600, color: 'oklch(0.3 0.012 90)' }}>{selLabel}</span>
            <button
              onClick={() => p.setSelected(new Set(p.shown.map((x) => x.v.id)))}
              style={{ cursor: 'pointer', border: 0, background: 'none', padding: 0, fontSize: rem(12), fontFamily: 'inherit', color: 'oklch(0.45 0.07 245)', textDecoration: 'underline' }}
            >
              vse
            </button>
            <button
              onClick={() => p.setSelected(new Set())}
              style={{ cursor: 'pointer', border: 0, background: 'none', padding: 0, fontSize: rem(12), fontFamily: 'inherit', color: 'oklch(0.5 0.012 90)', textDecoration: 'underline' }}
            >
              nič
            </button>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => selCount > 0 && setBulkOpen((o) => !o)}
              style={{
                cursor: selCount > 0 ? 'pointer' : 'default',
                padding: '6px 11px',
                borderRadius: 7,
                fontSize: rem(12.5),
                fontWeight: 500,
                fontFamily: 'inherit',
                border: 0,
                background: selCount > 0 ? 'oklch(0.35 0.05 245)' : 'oklch(0.93 0.006 95)',
                color: selCount > 0 ? 'oklch(1 0 0)' : 'oklch(0.6 0.012 90)',
              }}
            >
              Oznake…
            </button>
          </div>
          {bulkOpen && selCount > 0 ? (
            <div style={{ position: 'absolute', left: 10, right: 10, bottom: 46, zIndex: 20 }}>
              <div style={{ fontSize: rem(11), letterSpacing: '0.05em', fontWeight: 600, color: 'oklch(0.55 0.012 90)', marginBottom: 6, background: 'oklch(1 0 0)', borderRadius: 6, padding: '4px 8px', border: '1px solid oklch(0.92 0.006 95)' }}>
                OZNAKE ZA {selCount} IZBRANIH
              </div>
              <TagPicker
                kind="volunteer"
                tags={p.tags}
                targets={bulkTargets}
                onApply={(delta) => applyTags(bulkTargets, delta)}
                onClose={() => setBulkOpen(false)}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          flexShrink: 0,
          borderTop: '1px solid oklch(0.94 0.005 95)',
          padding: '8px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
        }}
      >
        {canEdit ? (
          <button onClick={p.openImport} style={smallBtn}>
            Uvoz CSV
          </button>
        ) : null}
        {can('comms:write') ? (
          <button onClick={p.openTriage} style={{ ...smallBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
            Nabiralnik
            {p.triageCount > 0 ? (
              <span
                style={{
                  fontFamily: MONO,
                  fontSize: rem(11),
                  fontWeight: 700,
                  color: 'oklch(1 0 0)',
                  background: 'oklch(0.55 0.14 25)',
                  borderRadius: 10,
                  padding: '1px 5px',
                }}
              >
                {p.triageCount}
              </span>
            ) : null}
          </button>
        ) : null}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: rem(11.5), color: 'oklch(0.6 0.012 90)', whiteSpace: 'nowrap' }}>
          {p.shown.length} / {p.rows.length}
        </span>
      </div>
    </div>
  );
}
